import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { fetchFinances } from '../../lib/dataProvider';
import { reglerConversion, sortiesRattachables } from '../../lib/risqueHuile';

const label = 'text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const field =
  'mt-1 h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm outline-none focus:border-slate-900 focus:bg-white';
const MODES = ['Espèces', 'Chèque', 'Virement', 'Traite'];

// AUDIT C05 — une dette de conversion ne se solde jamais sans sortie de trésorerie :
// soit la sortie est créée ici, soit elle est rattachée à une sortie déjà saisie.
export const ReglerConversionDialog = ({ dette, utilisateur, onClose, onDone }) => {
  const [comptes, setComptes] = useState([]);
  const [candidats, setCandidats] = useState([]);
  const [occupe, setOccupe] = useState(false);
  const [f, setF] = useState({
    montant: Number(dette.reste || 0).toFixed(3),
    date: new Date().toISOString().slice(0, 10),
    compteId: '',
    mode: 'Espèces',
    reference: '',
    source: 'creer',
    lien: '',
  });

  useEffect(() => {
    fetchFinances().then((fin) => {
      const liste = fin.soldes ?? fin.comptes ?? [];
      setComptes(liste);
      setF((p) => ({ ...p, compteId: p.compteId || (liste[0]?.id ?? '') }));
    });
  }, []);

  const chercher = useCallback(() => {
    sortiesRattachables({ tiersId: dette.tiersId, compteId: f.compteId, montant: f.montant }).then(setCandidats);
  }, [dette.tiersId, f.compteId, f.montant]);

  useEffect(() => {
    if (f.source === 'rattacher') chercher();
  }, [f.source, chercher]);

  const enregistrer = async (e) => {
    e.preventDefault();
    const rattacher = f.source === 'rattacher' ? candidats.find((c) => `${c.bucket}:${c.id}` === f.lien) : null;
    if (f.source === 'rattacher' && !rattacher)
      return toast.error('Choisissez la sortie de trésorerie à rattacher (montant exact, même compte).');
    setOccupe(true);
    const { error, reste } = await reglerConversion({
      id: dette.id,
      montant: f.montant,
      date: f.date,
      compteId: f.compteId,
      mode: f.mode,
      reference: f.reference,
      rattacher,
      utilisateur,
    });
    setOccupe(false);
    if (error) return toast.error(error);
    toast.success(
      rattacher
        ? `Règlement rattaché à la sortie ${rattacher.id} — aucune seconde sortie créée. Reste ${Number(reste).toFixed(3)} TND`
        : `Règlement enregistré AVEC sa sortie de trésorerie — reste ${Number(reste).toFixed(3)} TND (aucun kilo retiré du compte huile)`
    );
    onDone();
    onClose();
  };

  return (
    <div
      data-testid="regler-conversion-overlay"
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-auto bg-slate-900/60 p-4"
    >
      <form onSubmit={enregistrer} data-testid="regler-conversion-form" className="w-full max-w-xl border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="font-heading text-base font-black tracking-tight text-slate-900">
              Régler la conversion {dette.id}
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              {dette.client} — reste {fmt(dette.reste, 3)} TND
            </p>
          </div>
          <button
            type="button"
            data-testid="regler-conversion-close"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-2">
          <div>
            <label className={label}>Montant réglé (TND)</label>
            <input
              data-testid="regler-conversion-montant"
              type="number"
              step="0.001"
              min="0"
              value={f.montant}
              onChange={(e) => setF({ ...f, montant: e.target.value })}
              className={field}
            />
          </div>
          <div>
            <label className={label}>Date du règlement</label>
            <input
              data-testid="regler-conversion-date"
              type="date"
              value={f.date}
              onChange={(e) => setF({ ...f, date: e.target.value })}
              className={field}
            />
          </div>
          <div>
            <label className={label}>Caisse / banque qui paie</label>
            <select
              data-testid="regler-conversion-compte"
              value={f.compteId}
              onChange={(e) => setF({ ...f, compteId: e.target.value, lien: '' })}
              className={field}
            >
              <option value="">— Choisir —</option>
              {comptes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                  {c.solde != null ? ` — ${fmt(c.solde, 3)} TND` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Sortie de trésorerie</label>
            <select
              data-testid="regler-conversion-source"
              value={f.source}
              onChange={(e) => setF({ ...f, source: e.target.value, lien: '' })}
              className={field}
            >
              <option value="creer">Créer la sortie maintenant</option>
              <option value="rattacher">Rattacher une sortie déjà saisie</option>
            </select>
          </div>
          {f.source === 'creer' ? (
            <>
              <div>
                <label className={label}>Mode</label>
                <select
                  data-testid="regler-conversion-mode"
                  value={f.mode}
                  onChange={(e) => setF({ ...f, mode: e.target.value })}
                  className={field}
                >
                  {MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Référence (chèque, pièce…)</label>
                <input
                  data-testid="regler-conversion-reference"
                  value={f.reference}
                  onChange={(e) => setF({ ...f, reference: e.target.value })}
                  className={field}
                  placeholder={dette.id}
                />
              </div>
            </>
          ) : (
            <div className="sm:col-span-2">
              <label className={label}>Sortie existante non affectée (montant exact)</label>
              <select
                data-testid="regler-conversion-lien"
                value={f.lien}
                onChange={(e) => setF({ ...f, lien: e.target.value })}
                className={field}
              >
                <option value="">— Choisir —</option>
                {candidats.map((c) => (
                  <option key={`${c.bucket}:${c.id}`} value={`${c.bucket}:${c.id}`}>
                    {c.date} · {c.id} · {fmt(c.montant, 3)} TND · {c.libelle || c.piece || '—'}
                  </option>
                ))}
              </select>
              {!candidats.length && (
                <p data-testid="regler-conversion-aucun-candidat" className="mt-1 text-[11px] text-amber-700">
                  Aucune sortie rattachable : même tiers, même compte et montant exactement égal au montant réglé
                  sont exigés. Créez la sortie, ou corrigez le compte / le montant.
                </p>
              )}
            </div>
          )}
        </div>

        <p data-testid="regler-conversion-avertissement" className="mx-4 mb-3 border border-slate-300 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
          Le règlement et sa sortie de trésorerie sont enregistrés par la base dans une seule opération. Aucun
          kilo n'est retiré du compte huile du client.
        </p>

        <div className="flex items-center gap-3 border-t border-slate-200 px-4 py-3">
          <button
            type="submit"
            data-testid="regler-conversion-valider"
            disabled={occupe}
            className="h-10 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white disabled:opacity-50"
          >
            {occupe ? 'Enregistrement…' : 'Enregistrer le règlement'}
          </button>
        </div>
      </form>
    </div>
  );
};
